import React, { useEffect, useRef, useState } from "react";
import "../../_css/Overview.css";
import "../../_css/TeacherManagement.css";
import FeatureCard from "./FeatureCard";
import { getQuestionStatsBySubject } from "../../requests";
import { notifyError } from "../helper/Toaster";
import { deleteAuthToken } from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";

const formatPercent = (edited, total) => {
  if (!total || total === 0) return "—";
  return `${((edited / total) * 100).toFixed(1)}%`;
};

const ReviewAccuracyTrackingUI = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState("");
  const [debouncedSubject, setDebouncedSubject] = useState("");
  const [summary, setSummary] = useState({
    total_questions: 0,
    edited_questions: 0,
    not_edited_questions: 0,
  });

  const timeoutRef = useRef(null);

  const fetchStats = async (pageVal, subject) => {
    dispatch(startLoader());
    try {
      const params = { page: pageVal, limit };
      if (subject?.trim()) {
        params.subject = subject.trim();
      }

      const res = await getQuestionStatsBySubject(params);
      setRows(res.data?.data || []);
      setTotalCount(res.data?.totalCount ?? 0);
      setSummary(
        res.data?.platformTotals || {
          total_questions: 0,
          edited_questions: 0,
          not_edited_questions: 0,
        }
      );
    } catch (error) {
      if (error?.response?.status === 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }
      if (error?.response?.status === 400) {
        notifyError(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Only admins can access this page."
        );
        return;
      }
      notifyError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to load review & accuracy data"
      );
    } finally {
      dispatch(stopLoader());
    }
  };

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setDebouncedSubject(subjectFilter);
      setPage(1);
    }, 800);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [subjectFilter]);

  useEffect(() => {
    fetchStats(page, debouncedSubject);
  }, [page, debouncedSubject]);

  return (
    <div className="tm-main">
      <div className="overview-head">
        <div className="overview-head-left">Review & Accuracy Tracking</div>
        {/* <div className="overview-head-right">
          <input
            className="teacher-head-search"
            placeholder="Filter by subject"
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
          />
        </div> */}
      </div>

      <p
        style={{
          fontSize: "12px",
          color: "#6B7280",
          margin: "0 0 12px 0",
          width: "100%",
        }}
      >
        Summary cards reflect totals for the current page of subjects.
      </p>
      <div className="overview-card-con di-flex">
        <FeatureCard
          title="Total Questions"
          desc={summary.total_questions}
          color="#F0FAFF"
        />
        <FeatureCard
          title="Edited (Reviewed)"
          desc={summary.edited_questions}
          color="#ECFDF5"
        />
        {/* <FeatureCard
          title="Not Edited"
          desc={summary.not_edited_questions}
          color="#FEF2F2"
        /> */}
        <FeatureCard
          title="Review Rate"
          desc={formatPercent(
            summary.edited_questions,
            summary.total_questions
          )}
          color="#FEFCE8"
        />
      </div>

      <div className="teacher-container" style={{ padding: 0, width: "100%" }}>
        <div className="teacher-table" style={{ borderTop: "none" }}>
          <div className="teacher-table-head">
            <div className="teacher-table-head-cell" style={{ width: "8%" }}>
              S. No
            </div>
            <div className="teacher-table-head-cell" style={{ width: "22%" }}>
              Subject
            </div>
            <div className="teacher-table-head-cell" style={{ width: "18%" }}>
              Total Questions
            </div>
            <div className="teacher-table-head-cell" style={{ width: "18%" }}>
              Edited Questions
            </div>
            <div className="teacher-table-head-cell" style={{ width: "18%" }}>
              Not Edited
            </div>
            <div className="teacher-table-head-cell" style={{ width: "16%" }}>
              Review Rate
            </div>
          </div>

          {rows.length === 0 && (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#9CA3AF",
                width: "100%",
              }}
            >
              No data found
            </div>
          )}

          {rows.length > 0 &&
            rows.map((row, i) => (
              <div
                key={`${row.subject}-${i}`}
                className="teacher-table-row"
                style={{
                  borderBottom: i === rows.length - 1 ? "none" : undefined,
                }}
              >
                <div
                  className="teacher-table-row-cell"
                  style={{ width: "8%" }}
                >
                  {(page - 1) * limit + i + 1}
                </div>
                <div
                  className="teacher-table-row-cell"
                  style={{ width: "22%" }}
                >
                  {row.subject}
                </div>
                <div
                  className="teacher-table-row-cell"
                  style={{ width: "18%" }}
                >
                  {row.total_questions}
                </div>
                <div
                  className="teacher-table-row-cell"
                  style={{ width: "18%" }}
                >
                  {row.edited_questions}
                </div>
                <div
                  className="teacher-table-row-cell"
                  style={{ width: "18%" }}
                >
                  {row.not_edited_questions}
                </div>
                <div
                  className="teacher-table-row-cell"
                  style={{ width: "16%" }}
                >
                  {formatPercent(row.edited_questions, row.total_questions)}
                </div>
              </div>
            ))}
        </div>

        <div className="di-flex" style={{ width: "100%", marginTop: "16px" }}>
          <div style={{ color: "#6B7280" }}>
            Showing {(page - 1) * limit + 1} to{" "}
            {rows.length + (page - 1) * limit} out of {totalCount} subjects
          </div>
          <div
            className="di-flex"
            style={{ justifyContent: "end", gap: "10px" }}
          >
            <button
              disabled={page === 1}
              className="my-btn"
              style={{
                backgroundColor: "#E0F5FE",
                color: "#01A7DE",
                opacity: page === 1 ? 0.4 : 1,
              }}
              onClick={() => setPage((p) => (p > 1 ? p - 1 : p))}
            >
              Previous
            </button>
            <button
              disabled={rows.length + (page - 1) * limit >= totalCount}
              className="my-btn"
              style={{
                backgroundColor: "#E0F5FE",
                color: "#01A7DE",
                opacity:
                  rows.length + (page - 1) * limit >= totalCount ? 0.4 : 1,
              }}
              onClick={() =>
                setPage((p) =>
                  rows.length + (p - 1) * limit < totalCount ? p + 1 : p
                )
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewAccuracyTrackingUI;
